import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const UpdateProfile = () => {
  const { user, updateProfile } = useAuth();

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company_name: user?.company_name || '',
    company_address: user?.company_address || '',
    payment_details: user?.payment_details || '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
    if (success) {
      setSuccess(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setSuccess(false);

    const result = await updateProfile(formData);

    if (result.success) {
      setSuccess(true);
    } else {
      let newErrors = result.errors || {};
      if (result.message) {
        newErrors.general = result.message;
      }
      setErrors(newErrors);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700 p-3 sm:p-5">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 sm:p-10 w-full max-w-md transform transition-all duration-500 hover:scale-105">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-gray-900 dark:text-white text-2xl sm:text-3xl font-bold mb-2">Update Profile</h2>
          <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">Update your account details</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {success && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm text-green-700 dark:text-green-300 text-center">
              Profile updated successfully!
            </div>
          )}

          {errors.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300 text-center">
              {errors.general}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-semibold text-gray-900 dark:text-white">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              className={`w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none focus:ring-3 focus:ring-primary-100 dark:focus:ring-primary-500/20 ${
                errors.name
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20 focus:border-red-500 text-gray-900 dark:text-white'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 focus:border-primary-500 dark:focus:border-primary-400 text-gray-900 dark:text-white'
              }`}
              required
            />
            {errors.name && (
              <span className="text-red-500 dark:text-red-400 text-sm font-medium">{errors.name[0]}</span>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-semibold text-gray-900 dark:text-white">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              className={`w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none focus:ring-3 focus:ring-primary-100 dark:focus:ring-primary-500/20 ${
                errors.email
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20 focus:border-red-500 text-gray-900 dark:text-white'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 focus:border-primary-500 dark:focus:border-primary-400 text-gray-900 dark:text-white'
              }`}
              required
            />
            {errors.email && (
              <span className="text-red-500 dark:text-red-400 text-sm font-medium">{errors.email[0]}</span>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="company_name" className="block text-sm font-semibold text-gray-900 dark:text-white">
              Company Name
            </label>
            <input
              type="text"
              id="company_name"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              placeholder="e.g. Acme Ltd"
              className="w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none focus:ring-3 focus:ring-primary-100 dark:focus:ring-primary-500/20 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 focus:border-primary-500 dark:focus:border-primary-400 text-gray-900 dark:text-white"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="company_address" className="block text-sm font-semibold text-gray-900 dark:text-white">
              Company Address
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">Shown at the bottom of printed and emailed invoices.</p>
            <textarea
              id="company_address"
              name="company_address"
              rows={3}
              value={formData.company_address}
              onChange={handleChange}
              placeholder="e.g. 123 High Street, London, EC1A 1BB"
              className="w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none focus:ring-3 focus:ring-primary-100 dark:focus:ring-primary-500/20 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 focus:border-primary-500 dark:focus:border-primary-400 text-gray-900 dark:text-white resize-none"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="payment_details" className="block text-sm font-semibold text-gray-900 dark:text-white">
              Payment Details
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">Bank name, account number, sort code, or any other payment instructions shown on invoices.</p>
            <textarea
              id="payment_details"
              name="payment_details"
              rows={4}
              value={formData.payment_details}
              onChange={handleChange}
              placeholder="e.g. Bank: Lloyds\nAccount: 12345678\nSort Code: 12-34-56"
              className="w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none focus:ring-3 focus:ring-primary-100 dark:focus:ring-primary-500/20 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 focus:border-primary-500 dark:focus:border-primary-400 text-gray-900 dark:text-white resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold text-base transition-all duration-300 hover:from-blue-600 hover:to-purple-700 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="w-full bg-transparent text-gray-600 dark:text-gray-400 py-2 px-6 rounded-lg font-medium text-sm transition-all duration-300 hover:text-gray-900 dark:hover:text-white"
          >
            Back to Dashboard
          </button>
        </form>
      </div>
    </div>
  );
};

export default UpdateProfile;
